import { Component, DestroyRef, OnInit, inject, ChangeDetectionStrategy } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  FormBuilder,
  FormControl,
  FormGroup,
  Validators,
  FormsModule,
  ReactiveFormsModule,
} from '@angular/forms';
import { MatButton } from '@angular/material/button';
import { MAT_DIALOG_DATA, MatDialogConfig, MatDialogRef } from '@angular/material/dialog';
import { TranslatePipe } from '@ngx-translate/core';
import { map } from 'rxjs/operators';
import { UsersBackendService } from '@core/providers/backend/users-backend.service';
import {
  FoodbackStatistic,
  IFoodbackContactStatus,
} from '@features/venue/foodbacks/all-foodbacks/foodback-statistic';
import {
  DialogComponent,
  DialogTitleDirective,
  DialogContentDirective,
  DialogActionsDirective,
} from '@shared/components/dialog/dialog.component';
import { SelectComponent } from '@shared/components/select/select.component';
import { TextareaWithCounterComponent } from '@shared/components/textarea-with-counter/textarea-with-counter.component';
import { CONSTANTS } from '@shared/constants';
import { IDropdownItem } from '@shared/interfaces/dropdown/dropdown-item.interface';

export const customContactDialogConfig: MatDialogConfig = {
  width: '550px',
  maxWidth: 'calc(100vw - 32px)',
  disableClose: false,
};

type ContactForm = FormGroup<{
  isContacted: FormControl<IDropdownItem>;
  ownerUuid: FormControl<IDropdownItem | null>;
  note: FormControl<string | null>;
}>;

export enum ContactedStatus {
  ALL_STATUS = 'all',
  CONTACTED = 'contacted',
  NOT_CONTACTED = '!contacted',
}

@Component({
  selector: 'app-foodback-update-contact',
  templateUrl: './foodback-update-contact.component.html',
  styleUrls: ['./foodback-update-contact.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    MatButton,
    DialogComponent,
    DialogTitleDirective,
    DialogContentDirective,
    FormsModule,
    ReactiveFormsModule,
    SelectComponent,
    TextareaWithCounterComponent,
    DialogActionsDirective,
    TranslatePipe,
  ],
})
export class FoodbackUpdateContactComponent implements OnInit {
  private formBuilder = inject(FormBuilder);
  private mdDialogRef =
    inject<MatDialogRef<FoodbackUpdateContactComponent, IFoodbackContactStatus>>(MatDialogRef);
  private usersBackendService = inject(UsersBackendService);
  private dataDialog = inject<FoodbackStatistic>(MAT_DIALOG_DATA);
  private destroyRef = inject(DestroyRef);

  contactStatuses: IDropdownItem[] = [
    {
      id: ContactedStatus.CONTACTED,
      translationKey: 'HOME.FOODBACKS.CONTACTED.STATUS_CONTACTED',
    },
    {
      id: ContactedStatus.NOT_CONTACTED,
      translationKey: 'HOME.FOODBACKS.CONTACTED.STATUS_NOT_CONTACTED',
    },
  ];
  readonly CONSTANTS = CONSTANTS;
  form: ContactForm = this.getContactedForm();
  users: IDropdownItem[] = [];

  get isContacted(): boolean {
    return this.form.controls.isContacted.value.id === ContactedStatus.CONTACTED;
  }

  ngOnInit(): void {
    this.form.controls.isContacted.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.requireOwnerOnlyWhenContacted());
    if (this.dataDialog.venueUuid) {
      this.getAndSetOwners(this.dataDialog.venueUuid);
    }
  }

  hide() {
    this.mdDialogRef.close();
  }

  save() {
    this.form.markAllAsTouched();

    if (this.form.valid) {
      const status: IFoodbackContactStatus = this.isContacted
        ? {
            ownerUuid: this.form.controls.ownerUuid.value?.id ?? null,
            note: this.form.controls.note.value || null,
            flags: [ContactedStatus.CONTACTED],
          }
        : { ownerUuid: null, note: null, flags: [] };
      this.mdDialogRef.close(status);
    }
  }

  /** The backend keeps an owner and a note only on a contacted foodback. */
  private requireOwnerOnlyWhenContacted(): void {
    const owner = this.form.controls.ownerUuid;
    owner.setValidators(this.isContacted ? [Validators.required] : []);
    owner.updateValueAndValidity({ emitEvent: false });
  }

  /** Built from the foodback straight away, so the dialog opens in its real state. */
  private getContactedForm(): ContactForm {
    const contacted = !!this.dataDialog.flags?.length;
    return this.formBuilder.group({
      isContacted: this.formBuilder.nonNullable.control(
        contacted ? this.contactStatuses[0] : this.contactStatuses[1],
        [Validators.required]
      ),
      ownerUuid: this.formBuilder.control<IDropdownItem | null>(
        null,
        contacted ? [Validators.required] : []
      ),
      note: this.formBuilder.control<string | null>(this.dataDialog.note ?? ''),
    });
  }

  private getAndSetOwners(venueUuid: string) {
    this.usersBackendService
      .getListOfPotentialRecipients$(venueUuid)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        map(res =>
          res.users
            .map(user => ({
              id: user.uuid,
              text: user.name ? user.name : user.email,
            }))
            .sort((a, b) => a.text.toLowerCase().localeCompare(b.text.toLowerCase()))
        )
      )
      .subscribe(res => {
        this.users = res;
        this.selectCurrentOwner();
      });
  }

  private selectCurrentOwner() {
    if (this.dataDialog.ownerUuid) {
      const user = this.users.find(usr => usr.id === this.dataDialog.ownerUuid);

      this.form.controls.ownerUuid.setValue(user ?? null);
    }
  }
}
